import type { ScreenTourMap } from './types';

/**
 * Préstamos (resto del área): préstamo nuevo, detalle y recibo de abonos, gastos, cajas, correcciones,
 * equipos de las rutas, preferencias de avisos y centro de ayuda. Textos en `tours-loans.json`.
 */
export const loanMoreTours: ScreenTourMap = {
  '/loans/new': {
    key: 'screen-loan-new',
    steps: [
      { text: 'tours.loanNew.welcome' },
      { text: 'tours.loanNew.route', target: 'loan-route' },
      { text: 'tours.loanNew.client', target: 'loan-client' },
      { text: 'tours.loanNew.quota', target: 'loan-new-quota' },
      { text: 'tours.loanNew.position', target: 'loan-new-position' },
      { text: 'tours.loanNew.type', target: 'loan-type' },
      { text: 'tours.loanNew.installments', target: 'loan-new-installments' },
      // En sedes que venden artículos no hay «Valor solicitado»: se explica en el mismo paso.
      { text: 'tours.loanNew.value', target: 'loan-value' },
      { text: 'tours.loanNew.utility', target: 'loan-new-utility' },
      { text: 'tours.loanNew.fee', target: 'loan-new-fee' },
      { text: 'tours.loanNew.totals', target: 'loan-new-totals' },
      { text: 'tours.loanNew.save', target: 'save' },
    ],
  },
  '/loans/:id/payments': {
    key: 'screen-loan-detail',
    steps: [
      { text: 'tours.loanDetail.welcome' },
      { text: 'tours.loanDetail.summary', target: 'loan-detail-summary' },
      { text: 'tours.loanDetail.pay', target: 'loan-detail-pay', whenMissing: 'center' },
      { text: 'tours.loanDetail.newLoan', target: 'loan-detail-new-loan' },
      { text: 'tours.loanDetail.table', target: 'loan-detail' },
      { text: 'tours.loanDetail.state', target: 'loan-detail-col-state' },
      { text: 'tours.loanDetail.actions', target: 'loan-detail-actions', whenMissing: 'center' },
      { text: 'tours.loanDetail.export', target: 'loan-detail-export' },
    ],
  },
  '/loans/payments/:id/receipt': {
    key: 'screen-payment-receipt',
    steps: [
      { text: 'tours.paymentReceipt.welcome' },
      { text: 'tours.paymentReceipt.ticket', target: 'payment-receipt-ticket' },
      { text: 'tours.paymentReceipt.share', target: 'payment-receipt-share' },
      { text: 'tours.paymentReceipt.download', target: 'payment-receipt-download' },
    ],
  },
  '/loans/expenses': {
    key: 'screen-expenses',
    steps: [
      { text: 'tours.expenses.welcome' },
      { text: 'tours.expenses.new', target: 'expenses-new' },
      { text: 'tours.expenses.office', target: 'expenses-office' },
      { text: 'tours.expenses.route', target: 'expenses-route' },
      { text: 'tours.expenses.dates', target: 'expenses-start' },
      { text: 'tours.expenses.type', target: 'expenses-type' },
      { text: 'tours.expenses.query', target: 'expenses-query' },
      { text: 'tours.expenses.stats', target: 'expenses-stats', whenMissing: 'center' },
      { text: 'tours.expenses.search', target: 'expenses-search' },
      { text: 'tours.expenses.table', target: 'expenses' },
      { text: 'tours.expenses.actions', target: 'expenses-actions', whenMissing: 'center' },
      { text: 'tours.expenses.export', target: 'expenses-export' },
    ],
  },
  '/loans/expenses/new': {
    key: 'screen-expense-new',
    steps: [
      { text: 'tours.expenseNew.welcome' },
      { text: 'tours.expenseNew.boxKind', target: 'expense-new-box-kind' },
      { text: 'tours.expenseNew.office', target: 'expense-new-office' },
      { text: 'tours.expenseNew.balance', target: 'expense-new-balance' },
      { text: 'tours.expenseNew.value', target: 'expense-new-value' },
      { text: 'tours.expenseNew.description', target: 'expense-new-description' },
      { text: 'tours.expenseNew.affectMain', target: 'expense-new-affect-main' },
      { text: 'tours.expenseNew.routes', target: 'expense-new-routes' },
      { text: 'tours.expenseNew.routeActions', target: 'expense-new-routes' },
      { text: 'tours.expenseNew.save', target: 'expense-new-save' },
      { text: 'tours.expenseNew.mainBox', target: 'expense-new-main-box' },
    ],
  },
  '/loans/expenses/:id/edit': {
    key: 'screen-expense-edit',
    steps: [
      { text: 'tours.expenseEdit.welcome' },
      { text: 'tours.expenseEdit.info', target: 'expense-edit-info' },
      { text: 'tours.expenseEdit.howTo' },
      { text: 'tours.expenseEdit.back', target: 'expense-edit-back' },
    ],
  },
  '/loans/cash-boxes': {
    key: 'screen-cash-boxes',
    steps: [
      { text: 'tours.cashBoxes.welcome' },
      { text: 'tours.cashBoxes.office', target: 'cash-boxes-office' },
      { text: 'tours.cashBoxes.new', target: 'cash-boxes-new' },
      { text: 'tours.cashBoxes.stats', target: 'cash-boxes-stats', whenMissing: 'center' },
      { text: 'tours.cashBoxes.search', target: 'cash-boxes-search' },
      { text: 'tours.cashBoxes.table', target: 'cash-boxes' },
      { text: 'tours.cashBoxes.actions', target: 'cash-boxes-actions', whenMissing: 'center' },
      { text: 'tours.cashBoxes.export', target: 'cash-boxes-export' },
    ],
  },
  '/loans/cash-box-expenses': {
    key: 'screen-cash-movements',
    steps: [
      { text: 'tours.cashMovements.welcome' },
      { text: 'tours.cashMovements.office', target: 'cash-movements-office' },
      { text: 'tours.cashMovements.route', target: 'cash-movements-route' },
      { text: 'tours.cashMovements.dates', target: 'cash-movements-start' },
      { text: 'tours.cashMovements.type', target: 'cash-movements-type' },
      { text: 'tours.cashMovements.onlyMain', target: 'cash-movements-only-main' },
      { text: 'tours.cashMovements.query', target: 'cash-movements-query' },
      { text: 'tours.cashMovements.stats', target: 'cash-movements-stats', whenMissing: 'center' },
      { text: 'tours.cashMovements.search', target: 'cash-movements-search' },
      { text: 'tours.cashMovements.table', target: 'cash-movements' },
      { text: 'tours.cashMovements.actions', target: 'cash-movements-actions', whenMissing: 'center' },
      { text: 'tours.cashMovements.export', target: 'cash-movements-export' },
    ],
  },
  '/loans/cash-box-expenses/new': {
    key: 'screen-cash-movement-new',
    steps: [
      { text: 'tours.cashMovementNew.welcome' },
      { text: 'tours.cashMovementNew.office', target: 'cash-movement-new-office' },
      { text: 'tours.cashMovementNew.balance', target: 'cash-movement-new-balance' },
      { text: 'tours.cashMovementNew.operation', target: 'cash-movement-new-operation' },
      // «Ruta de destino» solo aparece con la operación que resta.
      { text: 'tours.cashMovementNew.destination', target: 'cash-movement-new-destination', whenMissing: 'center' },
      { text: 'tours.cashMovementNew.expenseType', target: 'cash-movement-new-expense-type' },
      { text: 'tours.cashMovementNew.value', target: 'cash-movement-new-value' },
      { text: 'tours.cashMovementNew.description', target: 'cash-movement-new-description' },
      { text: 'tours.cashMovementNew.save', target: 'cash-movement-new-save' },
      { text: 'tours.cashMovementNew.expense', target: 'cash-movement-new-expense' },
    ],
  },
  '/loans/payment-corrections': {
    key: 'screen-payment-corrections',
    guide: 'aprobar-correcciones',
    steps: [
      { text: 'tours.paymentCorrections.welcome' },
      { text: 'tours.paymentCorrections.office', target: 'payment-corrections-office' },
      { text: 'tours.paymentCorrections.routes', target: 'payment-corrections-routes', whenMissing: 'center' },
      { text: 'tours.paymentCorrections.date', target: 'payment-corrections-date' },
      { text: 'tours.paymentCorrections.query', target: 'payment-corrections-query' },
      { text: 'tours.paymentCorrections.search', target: 'payment-corrections-search' },
      { text: 'tours.paymentCorrections.table', target: 'payment-corrections' },
      { text: 'tours.paymentCorrections.actions', target: 'payment-corrections-actions', whenMissing: 'center' },
      { text: 'tours.paymentCorrections.export', target: 'payment-corrections-export' },
    ],
  },
  '/loans/correction-approvals': {
    key: 'screen-correction-approvals',
    guide: 'aprobar-correcciones',
    steps: [
      { text: 'tours.correctionApprovals.welcome' },
      { text: 'tours.correctionApprovals.tabs', target: 'correction-tabs' },
      { text: 'tours.correctionApprovals.refresh', target: 'correction-refresh' },
      { text: 'tours.correctionApprovals.search', target: 'correction-approvals-search' },
      { text: 'tours.correctionApprovals.table', target: 'correction-approvals' },
      { text: 'tours.correctionApprovals.change', target: 'correction-approvals-col-change' },
      { text: 'tours.correctionApprovals.approve', target: 'correction-approve', whenMissing: 'center' },
      { text: 'tours.correctionApprovals.reject', target: 'correction-reject', whenMissing: 'center' },
      { text: 'tours.correctionApprovals.export', target: 'correction-approvals-export' },
    ],
  },
  '/loans/route-devices': {
    key: 'screen-route-devices',
    steps: [
      { text: 'tours.routeDevices.welcome' },
      { text: 'tours.routeDevices.office', target: 'route-devices-office' },
      { text: 'tours.routeDevices.search', target: 'route-devices-search' },
      { text: 'tours.routeDevices.configure', target: 'route-devices-col-select' },
      { text: 'tours.routeDevices.loans', target: 'route-devices-col-allowMultipleLoans' },
      { text: 'tours.routeDevices.operations', target: 'route-devices-col-allowRepay' },
      { text: 'tours.routeDevices.access', target: 'route-devices-col-mobileSessionEnabled' },
      { text: 'tours.routeDevices.fee', target: 'route-devices-col-manualInsurance' },
      { text: 'tours.routeDevices.schedule', target: 'route-devices-col-enforceSchedule' },
      { text: 'tours.routeDevices.save', target: 'route-devices-save' },
      { text: 'tours.routeDevices.export', target: 'route-devices-export' },
    ],
  },
  '/notifications/preferences': {
    key: 'screen-notification-prefs',
    steps: [
      { text: 'tours.notificationPrefs.welcome' },
      { text: 'tours.notificationPrefs.list', target: 'notification-prefs-list' },
      { text: 'tours.notificationPrefs.channels', target: 'notification-prefs-channels' },
      { text: 'tours.notificationPrefs.noEmail', target: 'notification-prefs-no-email' },
      { text: 'tours.notificationPrefs.push', target: 'notification-prefs-push' },
      { text: 'tours.notificationPrefs.save', target: 'notification-prefs-save' },
    ],
  },
  '/ayuda': {
    key: 'screen-help-center',
    steps: [
      { text: 'tours.helpCenter.welcome' },
      { text: 'tours.helpCenter.button', target: 'screen-help' },
      { text: 'tours.helpCenter.search', target: 'help-search' },
      { text: 'tours.helpCenter.guides', target: 'help-guides' },
      { text: 'tours.helpCenter.topBar' },
    ],
  },
  '/ayuda/:topic': {
    key: 'screen-help-guide',
    steps: [
      { text: 'tours.helpGuide.welcome' },
      { text: 'tours.helpGuide.meta', target: 'help-guide-meta' },
      { text: 'tours.helpGuide.content', target: 'help-guide-content', whenMissing: 'center' },
      { text: 'tours.helpGuide.topBar' },
    ],
  },
};
